import { useMemo } from "react";
import { useQuery, keepPreviousData, type QueryClient } from "@tanstack/react-query";

import api from "@/api/base";

// Tracker cache plan — staleTime budgets.
//
// Why these values: with the localStorage persister wired in app.tsx,
// every visited dashboard / building page hydrates from disk in ~10ms
// and the BE refetch happens in the background once the persisted
// entry is older than ``staleTime``. The user always sees instant
// cached data; we just decide how fresh we want the background sync.
//
// The dashboard freshness banner already shows the true ``asOf``
// timestamp so the user isn't misled.
const STALE_TRACKER_DEFAULT = 1000 * 60 * 30;  //  30 min — dashboard, buildings, codes, summaries
const STALE_EXPOSURE_HISTORY = 1000 * 60 * 5;  //   5 min — matches dashboard query cadence
import {
  fetchPortalDashboard,
  fetchPortalBuildings,
  fetchPortalBuildingBins,
  fetchPortalBuildingCodes,
  fetchPortalExposureHistory,
  fetchPortalPortfolioSummary,
  fetchPortalPortfolioWins,
  fetchPortalPortfolioDrivers,
  type PortalDashboardData,
  type PortalBuildingsData,
  type PortalBuildingBinsData,
  type PortalBuildingsParams,
  type PortalBuildingCodesData,
  type PortalExposureHistoryData,
  type PortalPortfolioSummary,
  type PortalPortfolioWinsResponse,
  type PortalPortfolioDriversResponse,
} from "@/api/portal";
import { usePenaltyExposureQuery } from "@/queries/queries";
import { fetchPortalViolationDashboard } from "@/queries/violationMattersQueries";

export const usePortalDashboardQuery = () =>
  useQuery<PortalDashboardData>({
    queryKey: ["portal", "dashboard"],
    queryFn: fetchPortalDashboard,
    staleTime: STALE_TRACKER_DEFAULT,
  });

/**
 * Wave-8 Cluster A — drives the dashboard hero ``<ExposureSparkline>``.
 *
 * Powers the previously hardcoded ``SPARK_DATA = [929, 902, ...]`` array
 * on ``Page.tsx``. v1 returns a flat 12-bucket series with
 * ``historySupported=false`` (see ``fetchPortalExposureHistory`` for the
 * wire-shape rationale); the FE renders an honest zero-delta sparkline
 * instead of the previous fake numbers.
 *
 * staleTime mirrors the dashboard query — 5 min. The series rolls over
 * by month, so anything tighter than that just generates fetches that
 * always return the same shape. When v2 ships a per-month series we may
 * want a tighter staleTime (e.g. 30s, aligned with the dashboard live-
 * update polling rule).
 */
export const usePortalExposureHistory = () =>
  useQuery<PortalExposureHistoryData>({
    queryKey: ["portal", "exposure-history", "v3"],
    queryFn: fetchPortalExposureHistory,
    staleTime: STALE_EXPOSURE_HISTORY,
  });

/**
 * ONE READER: ``PortfolioCounters`` on the Dashboard's Buildings tab.
 *
 * THE DOCBLOCK THIS REPLACES WAS ALREADY FALSE AND PR-17 MADE IT FALSER. It
 * claimed the hook drove ``PortfolioHealthBreakdown``, ``PortfolioHandledWins``
 * AND ``PortfolioDrivers``; wins and drivers each grew their own hook
 * (``usePortalPortfolioWins`` / ``usePortalPortfolioDrivers``) and stopped
 * reading this one some time ago, and PR-17 retired the breakdown outright.
 *
 * WHAT THE PAYLOAD SUPPORTS, AND WHAT IT DOES NOT. ``PortfolioCounters`` renders
 * ``open_violations`` and ``deadlines_90d``, the second one itemised from
 * ``deadlines_by_type`` because the figure alone cannot be labelled honestly.
 * It deliberately renders NEITHER ``cleared_ytd`` nor anything derived from it:
 * the field ships beside a ``resolved_meta`` that defers or excludes all three
 * resolution detectors while ``hpd_cured_ytd`` on the same response is non-zero,
 * so the 0 measures a missing feed rather than a quiet year. See the field's own
 * note on ``PortalPortfolioSummary``.
 *
 * ITS ``as_of`` IS NOT THE DASHBOARD'S. Measured the same minute, this endpoint
 * reported 07:30:25 UTC against ``/portal/dashboard/``'s 11:10:13 — three hours
 * forty older. Any surface rendering these counts must print THIS stamp, never
 * the rail's.
 *
 * Loading state is surfaced via ``isLoading`` / ``isError`` so consumers render
 * a skeleton rather than a fake fallback. staleTime mirrors the dashboard query
 * at 5 min — these aggregates are snapshot-derived and refresh alongside the
 * warmup beat cycle.
 */
export const usePortalPortfolioSummary = () =>
  useQuery<PortalPortfolioSummary>({
    queryKey: ["portal", "portfolio", "summary"],
    queryFn: fetchPortalPortfolioSummary,
    staleTime: STALE_TRACKER_DEFAULT,
  });

export const usePortalPortfolioWins = (limit: number = 3) =>
  useQuery<PortalPortfolioWinsResponse>({
    queryKey: ["portal", "portfolio", "wins", limit],
    queryFn: () => fetchPortalPortfolioWins(limit),
    staleTime: STALE_TRACKER_DEFAULT,
  });

export const usePortalPortfolioDrivers = (limit: number = 3) =>
  useQuery<PortalPortfolioDriversResponse>({
    queryKey: ["portal", "portfolio", "drivers", limit],
    queryFn: () => fetchPortalPortfolioDrivers(limit),
    staleTime: STALE_TRACKER_DEFAULT,
  });

/**
 * Wave-15 Track B — paginated buildings list. The dashboard's buildings
 * table now drives this hook with `{ page, pageSize: 10 }` so the
 * portfolio scales past 10–20 rows without shipping every building to
 * the client on every dashboard refresh.
 *
 * `keepPreviousData` keeps the prior page's rows visible while the next
 * page is loading — no flash to an empty table on every page-button
 * click. The query key includes the page params so each page is its
 * own cache entry; TanStack will GC stale pages per its default rules.
 *
 * Default `page=1, pageSize=10` matches the BE's contract. Call sites
 * that don't care (e.g. the legacy /portal/buildings list view, if
 * any) can keep calling `usePortalBuildingsQuery()` and get page 1.
 */
export const usePortalBuildingsQuery = (params: PortalBuildingsParams = {}) => {
  // Cache-slot isolation (2026-07-23): an unpaginated call sends NO
  // page/page_size on the wire and gets the legacy full-list envelope
  // back — WITHOUT ``total_pages``. The old key defaulted to
  // ``page ?? 1 / pageSize ?? 10``, which parked that legacy response
  // in the SAME slot the dashboard's paginated page-1 query reads.
  // Whoever fetched first owned the slot for the 30-min staleTime, so
  // when WelcomeWarmup (the post-login splash) or the chat
  // BuildingPickerPopover — both unpaginated, both needing the FULL
  // list — ran before the dashboard, ``totalPages`` came back
  // undefined and the pager (gated on ``totalPages >= 2``) vanished.
  // Distinct "all" slot for unpaginated callers ends the collision;
  // both response shapes stay cached, neither poisons the other.
  const paginated = params.page !== undefined || params.pageSize !== undefined;
  return useQuery<PortalBuildingsData>({
    // Wave-16 — q is part of the cache key so a different search term
    // hits a different cache slot. Empty/undefined q reuses the
    // legacy unfiltered key.
    // feat/portal-enforcement-split — sortBy/sortDir join the paginated
    // key so each (page, q, sort) combination is its own cache slot. They
    // default to the server default (``enforcement`` / ``desc``) so the
    // dashboard's first paint keys the SAME slot the prefetch warmed. The
    // unpaginated "all" slot is unsorted (dedup/chat consumers never sort),
    // so it stays a 4-tuple.
    queryKey: paginated
      ? [
          "portal",
          "buildings",
          params.page ?? 1,
          params.pageSize ?? 10,
          params.q ?? "",
          params.sortBy ?? "enforcement",
          params.sortDir ?? "desc",
        ]
      : ["portal", "buildings", "all", params.q ?? ""],
    queryFn: () => fetchPortalBuildings(params),
    staleTime: STALE_TRACKER_DEFAULT,
    placeholderData: keepPreviousData,
  });
};

/**
 * Wave-15 Track B — portfolio-wide BIN list for the portal layout's
 * cross-page dedup + chat sidebar context. NOT consumed by this
 * Track-C UI; exposed here so the layout / chat consumers have a
 * single import path once the BE PR lands.
 */
export const usePortalBuildingBins = () =>
  useQuery<PortalBuildingBinsData>({
    queryKey: ["portal", "building-bins"],
    queryFn: fetchPortalBuildingBins,
    staleTime: STALE_TRACKER_DEFAULT,
  });

/**
 * Prefetch every query the /portal dashboard mounts so a navigation INTO
 * the dashboard renders from cache in <100ms — no network on the critical
 * path. Called by the /welcome warm-up splash during its dwell (the splash
 * spends ≥5s preparing; this fills the cache in that window).
 *
 * Keys/fns/staleTimes MUST stay identical to the hooks above (this is why
 * the helper lives in this file, next to them) — otherwise the dashboard's
 * `useQuery` would key a different cache slot and miss the prefetch.
 *
 * Fire-and-forget: returns a Promise but callers don't need to await it.
 * TanStack renders cached data the instant it exists and background-
 * refetches the rest, so the dashboard paints immediately on arrival.
 * Mirrors the dashboard's initial params: buildings page 1 / size 10 /
 * empty search, wins+drivers limit 3.
 */
export function prefetchPortalDashboard(
  queryClient: QueryClient,
): Promise<unknown> {
  const pq = <T,>(queryKey: unknown[], queryFn: () => Promise<T>, staleTime: number) =>
    queryClient
      .prefetchQuery({ queryKey, queryFn, staleTime })
      .catch(() => undefined); // best-effort: a prefetch miss must never break the splash

  return Promise.all([
    pq(["portal", "dashboard"], fetchPortalDashboard, STALE_TRACKER_DEFAULT),
    pq(["portal", "exposure-history", "v3"], fetchPortalExposureHistory, STALE_EXPOSURE_HISTORY),
    pq(["portal", "portfolio", "summary"], fetchPortalPortfolioSummary, STALE_TRACKER_DEFAULT),
    pq(["portal", "portfolio", "wins", 3], () => fetchPortalPortfolioWins(3), STALE_TRACKER_DEFAULT),
    // NO `portfolio/drivers` PREFETCH. `PortfolioDrivers` is deleted and
    // `usePortalPortfolioDrivers` has no caller outside tests, so this warmed a
    // cache slot nothing reads — a round-trip per visit, on the surface a
    // 253-building customer opens first. The hook and the fetcher stay for the
    // day the block has a home again; the unconditional request does not.
    // feat/portal-enforcement-split — key MUST match the dashboard's
    // first-paint slot: page 1 / size 10 / empty q / default sort
    // (enforcement-desc). The fetch omits sort params (the BE default IS
    // enforcement-desc), so warm-up lands in the exact slot the table reads.
    pq(["portal", "buildings", 1, 10, "", "enforcement", "desc"], () => fetchPortalBuildings({ page: 1, pageSize: 10 }), STALE_TRACKER_DEFAULT),
    pq(["portal", "building-bins"], fetchPortalBuildingBins, STALE_TRACKER_DEFAULT),
    // PR-5 — the matter-based violation dashboard (this-month strip, aging
    // block, two-axis stat, DSNY insight, FR-15c ladder note). Key + staleTime
    // MUST match `usePortalViolationDashboardQuery` (5 min) so the dashboard's
    // first paint lands in the exact slot this warms. Best-effort: a flag-off
    // 404 is swallowed by `pq`'s catch, so it never breaks the splash.
    pq(
      ["portal", "portfolio", "violation-dashboard"],
      fetchPortalViolationDashboard,
      1000 * 60 * 5,
    ),
  ]);
}

export const usePortalBuildingCodesQuery = (
  buildingId: string,
  refresh?: boolean,
) =>
  useQuery<PortalBuildingCodesData>({
    queryKey: ["portal", "building-codes", buildingId, refresh],
    queryFn: () => fetchPortalBuildingCodes(buildingId, refresh),
    enabled: !!buildingId,
    staleTime: STALE_TRACKER_DEFAULT,
  });

// ---------------------------------------------------------------------------
// Sync-state aggregation (Portal V1 — sync-state visual layer).
//
// Aggregates the per-source `source` field from `usePenaltyExposureQuery`'s
// penalty-exposure block PLUS the whole-query state of
// `usePortalBuildingCodesQuery` into one of four visual states:
//
//   "syncing"  — at least one source is still in flight (whole query
//                loading, OR per-slot data not yet present)
//   "partial"  — some sources have landed, others are still in flight
//                or errored (mixed: at least one fresh + at least one
//                pending/errored)
//   "stale"    — all sources have responded but at least one errored
//                (the page is showing last-known-good for that bucket)
//   "fresh"    — every source resolved cleanly
//
// Sources counted: DOB Admin (composite of `dobNow` + `oathDob`), ECB
// (composite of `ecb` + `oathOtherAgency`), Boiler (`boiler`), and the
// Codes whole-query. Code-driven exposure is a forward projection
// (deterministic, never errors) — NOT included in the source count.
//
// IMPORTANT (gap analysis): the per-source freshness lives only in the
// ECB endpoint's `penaltyExposure.components.{slot}.source` field. The
// Codes endpoint returns `as_of` + `flags` on the wire (verified at
// backend/server/tracking/portal_views.py:817) but the FE type
// `PortalBuildingCodesData` doesn't surface them yet. For V1 we drive
// the codes axis off `isLoading`/`isError` only and document the gap.
// ---------------------------------------------------------------------------

export type SyncState = "syncing" | "partial" | "stale" | "fresh";

export interface SyncStateBreakdown {
  state: SyncState;
  /** How many of the tracked sources have landed cleanly. */
  freshCount: number;
  /** How many are still in flight. */
  pendingCount: number;
  /** How many errored on the most recent attempt. */
  staleCount: number;
  /** Total tracked sources (4 today: DOB Admin, ECB, Boiler, Codes). */
  totalCount: number;
  /**
   * The first slot that errored — useful for surfacing in the banner
   * subcopy ("DOB Admin didn't respond on the last sync"). For V1 we
   * don't render this; reserved for follow-up polish.
   */
  firstStaleSource: string | null;
  /** Triggers a refetch of every sync source. */
  retry: () => void;
}

const ERROR_SOURCE_VALUES: ReadonlySet<string> = new Set(["error"]);

/**
 * Read the aggregate sync state for a building's portal page.
 *
 * Single source of truth so the page banner, the per-section
 * indicators, and the section-level skeleton/data toggles all agree
 * on what state we're in.
 */
export const useSyncState = (
  buildingId: string | undefined,
): SyncStateBreakdown => {
  const codesQuery = usePortalBuildingCodesQuery(buildingId ?? "");
  const ecbQuery = usePenaltyExposureQuery(buildingId ?? undefined);

  return useMemo<SyncStateBreakdown>(() => {
    type SlotState = "fresh" | "pending" | "stale";
    const slots: Array<{ id: string; state: SlotState }> = [];

    // Codes axis — whole-query, no per-source granularity yet.
    if (codesQuery.isLoading) {
      slots.push({ id: "codes", state: "pending" });
    } else if (codesQuery.isError || !codesQuery.data) {
      slots.push({ id: "codes", state: "stale" });
    } else {
      slots.push({ id: "codes", state: "fresh" });
    }

    // Penalty exposure axis — three buckets, each composed of 1+ slots
    // from `penaltyExposure.components`. A bucket is `pending` while
    // the whole query is loading; `stale` if any contributing slot
    // reports source==='error'; `fresh` otherwise.
    const exposure = ecbQuery.data?.penaltyExposure;

    const resolveBucket = (
      id: string,
      sources: ReadonlyArray<string | undefined>,
    ): SlotState => {
      if (ecbQuery.isLoading) return "pending";
      // Whole-query failure (ECB endpoint 5xx) — every bucket is stale
      // because we have NO payload to read source from.
      if (ecbQuery.isError || !exposure) return "stale";
      // Filter out undefined (BE didn't include this slot — shouldn't
      // happen given the always-5-keys contract, but be defensive).
      const present = sources.filter((s): s is string => Boolean(s));
      if (present.length === 0) return "fresh"; // nothing to fail
      const anyError = present.some((s) => ERROR_SOURCE_VALUES.has(s));
      return anyError ? "stale" : "fresh";
    };

    slots.push({
      id: "dob_admin",
      state: resolveBucket("dob_admin", [
        exposure?.components.dobNow.source,
        exposure?.components.oathDob.source,
      ]),
    });
    slots.push({
      id: "ecb",
      state: resolveBucket("ecb", [
        exposure?.components.ecb.source,
        exposure?.components.oathOtherAgency.source,
      ]),
    });
    slots.push({
      id: "boiler",
      state: resolveBucket("boiler", [exposure?.components.boiler.source]),
    });

    const freshCount = slots.filter((s) => s.state === "fresh").length;
    const pendingCount = slots.filter((s) => s.state === "pending").length;
    const staleCount = slots.filter((s) => s.state === "stale").length;
    const firstStaleSource = slots.find((s) => s.state === "stale")?.id ?? null;

    let state: SyncState;
    if (pendingCount === slots.length) {
      // Every source still loading — full-Syncing.
      state = "syncing";
    } else if (pendingCount > 0) {
      // Mixed loading + landed — Partial.
      state = "partial";
    } else if (staleCount > 0) {
      // Everyone responded; at least one errored — Stale.
      state = "stale";
    } else {
      state = "fresh";
    }

    const retry = () => {
      void codesQuery.refetch();
      void ecbQuery.refetch();
    };

    return {
      state,
      freshCount,
      pendingCount,
      staleCount,
      totalCount: slots.length,
      firstStaleSource,
      retry,
    };
    // We intentionally depend on the resolved booleans + data refs;
    // refetch identities are stable across renders so we don't need
    // them in the deps list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    codesQuery.isLoading,
    codesQuery.isError,
    codesQuery.data,
    ecbQuery.isLoading,
    ecbQuery.isError,
    ecbQuery.data,
  ]);
};

// ---------------------------------------------------------------------------
// Warmup status — drives the Add Building "Collecting" step.
//
// The BE endpoint `GET /api/portal/buildings/<id>/warmup-status/` returns
// the per-source freshness for a newly added building. Sources are grouped
// by the BE into three buckets the FE renders as three rows: identity,
// exposure, inspections (Wave 6).
//
// CAMELCASE BOUNDARY (per `feedback_camelcase_axios_jsonfield`):
// the axios interceptor in `@/api/base` deep-camelizes every JSON
// response. The BE ships snake_case keys (`all_ready`, `pending_count`,
// `source_count`, etc.); the interceptor rewrites them. We type the
// FE-facing shape in camelCase verbatim — NO local normalizer — so
// every consumer of this hook (CollectingFrame today) speaks one
// vocabulary. If a future SSE consumer of the same shape lands, it
// must add a snake→camel normalizer at its own boundary; do NOT split
// the camelCase typing here.
// ---------------------------------------------------------------------------

export type WarmupGroupStatus = "ready" | "pending" | "error";

export interface WarmupSource {
  /** Source key as it appears in BE (camelized by the axios interceptor). */
  key: string;
  /** Per-source state — `ready` once the snapshot is fresh; `pending`
   * while in flight; `error` if the live fetch raised. */
  status: WarmupGroupStatus;
}

export interface WarmupGroup {
  /** Aggregate status: `ready` iff every source is fresh; `error` iff
   * any source errored; otherwise `pending`. */
  status: WarmupGroupStatus;
  /** True when `status === "ready"`. Mirrors the BE's convenience flag. */
  ready: boolean;
  /** Per-source breakdown — `<freshCount> of <totalCount> sources`. */
  freshCount: number;
  totalCount: number;
  /** Optional per-source rows for diagnostics. The CollectingFrame
   * doesn't surface individual rows today (3-tier UI per design); kept
   * on the type so the same response can power a debug overlay. */
  sources?: WarmupSource[];
}

export interface WarmupStatusResponse {
  /** Convenience flag — true iff every group's `ready` is true. */
  allReady: boolean;
  groups: {
    identity: WarmupGroup;
    exposure: WarmupGroup;
    inspections: WarmupGroup;
  };
  /** ISO timestamp of the most recent snapshot across all sources. */
  asOf?: string | null;
}

const fetchWarmupStatus = (buildingId: string): Promise<WarmupStatusResponse> =>
  api
    .get<WarmupStatusResponse>(
      `tracking/portal/buildings/${buildingId}/warmup-status/`,
    )
    .then((r) => r.data);

/**
 * Poll the warmup-status endpoint every 2s while the building is still
 * warming. Stops polling once `allReady === true` (refetchInterval
 * returns false), at which point the CollectingFrame's advance gate
 * fires and the dialog flips to the next step.
 *
 * `staleTime: 0` so a re-mount inside the same 2s window still
 * triggers a fresh fetch — we don't want to read a 1.9s-old snapshot
 * and falsely advance.
 */
export const useWarmupStatusQuery = (
  buildingId: string | undefined,
  enabled: boolean = true,
) =>
  useQuery<WarmupStatusResponse>({
    queryKey: ["portal", "warmup-status", buildingId],
    queryFn: () => fetchWarmupStatus(buildingId as string),
    enabled: Boolean(buildingId) && enabled,
    staleTime: 0,
    // Active-polling query — persisting a stale "still warming"
    // snapshot would falsely block the Collecting frame's advance
    // gate on next mount.
    meta: { persist: false },
    refetchIntervalInBackground: false,
    refetchInterval: (query) => {
      // Stop polling once the BE reports all groups ready. Returning
      // `false` from refetchInterval is the documented TanStack idiom
      // for "stop polling, this row is done."
      const data = query.state.data as WarmupStatusResponse | undefined;
      if (data?.allReady) return false;
      return 2000;
    },
  });
